/**
 * ProtectedRoute Component
 *
 * Route wrapper that requires authentication.
 * Redirects to login page if user is not authenticated.
 * Supports role-based access control with single role or role array.
 */

import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts';
import { SimpleLoadingOverlay } from './LoadingOverlay';

export interface ProtectedRouteProps {
  /** Content to render when authenticated */
  children: ReactNode;
  /** Whether authentication is required (default: true) */
  requireAuth?: boolean;
  /** Single role required to access the route (deprecated, use requireRoles) */
  requiredRole?: string;
  /** Array of roles - user must have at least one of these roles */
  requireRoles?: string[];
  /** Where to redirect if not authenticated (default: /login) */
  redirectTo?: string;
  /** Where to redirect if unauthorized (default: /unauthorized) */
  unauthorizedRedirect?: string;
}

/**
 * ProtectedRoute component
 *
 * @example
 * // Require authentication only
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * @example
 * // Require specific roles
 * <ProtectedRoute requireRoles={['admin', 'tenant_admin']}>
 *   <AdminPanel />
 * </ProtectedRoute>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requiredRole,
  requireRoles,
  redirectTo = '/login',
  unauthorizedRedirect = '/unauthorized',
}) => {
  const { isAuthenticated, loading, hasRole, user } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (loading) {
    return <SimpleLoadingOverlay show={true} message="Checking authentication..." />;
  }

  // Redirect to login if authentication required but not authenticated
  if (requireAuth && !isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role requirements
  // Support both legacy single role and new role array
  const rolesToCheck = requireRoles || (requiredRole ? [requiredRole] : null);

  if (rolesToCheck && rolesToCheck.length > 0) {
    // User must have at least one of the required roles
    const hasRequiredRole = rolesToCheck.some((role) => hasRole(role));

    if (!hasRequiredRole) {
      // Redirect to unauthorized page
      return <Navigate to={unauthorizedRedirect} state={{ from: location, requiredRoles: rolesToCheck, userRoles: user?.roles }} replace />;
    }
  }

  // Render protected content
  return <>{children}</>;
};
